import React, { useState, useEffect, useContext } from "react";
import {
  Typography,
  Box,
  Backdrop,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import axios from "axios";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import { TreatmentItem } from "../../components/treatment/TreatmentItem";
import { Add } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import { ThemeContext } from "../../context/ThemeContext";
import { getColors } from "../../themes/theme";
import { AddTreatmentDialog } from "../../components/treatment/AddTreatmentDialog";
import { useSnackbar } from "notistack";

const TreatmentPage = () => {
  const { token } = useAuthContext();
  const [loading, setLoading] = useState(true); // Loading state for fetching data
  const [treatments, setTreatments] = useState([]); // State to store exercises fetched from backend
  const [searchQuery, setSearchQuery] = useState(""); // State for search query
  const [filteredTreatments, setFilteredTreatments] = useState([]); // State for filtered exercises
  const { darkMode } = useContext(ThemeContext);
  const [openAddDialog, setOpenAddDialog] = useState(false); // New state for confirmation dialog
  const { enqueueSnackbar } = useSnackbar();

  const updateTreatment = (updatedTreatment) => {
    setTreatments((prevTreatments) => {
      let updatedTreatments;

      if (updatedTreatment.deleted) {
        // Remove the deleted exercise from the state
        updatedTreatments = prevTreatments.filter(
          (treatment) => treatment.id !== updatedTreatment.id
        );
      } else if (
        !prevTreatments.some(
          (treatment) => treatment.id === updatedTreatment.id
        )
      ) {
        // Add the new exercise if it doesn't already exist
        updatedTreatments = [...prevTreatments, updatedTreatment];
      } else {
        // Update the existing exercise
        updatedTreatments = prevTreatments.map((treatment) =>
          treatment.id === updatedTreatment.id ? updatedTreatment : treatment
        );
      }

      // Sort exercises alphabetically by name
      return updatedTreatments.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
      );
    });
  };

  useEffect(() => {
    // Fetch exercises from the backend
    if (!token) return;
    const fetchTreatments = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/treatment`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setTreatments(response.data);
        setFilteredTreatments(response.data); // Initialize filtered exercises
      } catch (err) {
        console.error("Error fetching treatments:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTreatments();
  }, [token]);

  useEffect(() => {
    // Filter exercises based on the search query
    setFilteredTreatments(
      treatments.filter((treatment) =>
        treatment.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, treatments]);

  const handleAddTreatment = async (data) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/treatment`,
        {
          ...data,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      updateTreatment(response.data); // Update the parent state
      enqueueSnackbar("Successfully Added!", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to add treatment!", { variant: "error" });
    }
  };

  const handleAddClicked = () => {
    setOpenAddDialog(true);
  };

  const handleCancelAdd = () => {
    setOpenAddDialog(false);
  };

  return (
    <>
      <Box
        sx={{
          pt: "60px",
          pl: 5,
          pr: 10,
        }}
      >
        <Backdrop sx={{ zIndex: 1400 }} open={loading}>
          <CircularProgress size={80} />
        </Backdrop>
        {!loading && (
          <>
            <Typography
              sx={{
                fontFamily: "Poppins",
                fontSize: "28px",
                color: darkMode
                  ? getColors.drawerTextDark
                  : getColors.thumbnailTextLight,
                fontWeight: 600,
                textAlign: "left",
                mb: 5,
              }}
            >
              Global Treatment Management
            </Typography>
            <Box display="flex" justifyContent="space-between" sx={{ mb: 3 }}>
              <TextField
                variant="filled"
                placeholder="Search treatments"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 0 }}>
                      <SearchIcon sx={{ mt: -2, ml: 1 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                startIcon={<Add />}
                variant="contained"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "18px",
                  fontWeight: 500,
                  px: 3,
                  py: 2,
                  background: darkMode ? "#2D2E30" : "#E6E6E6",
                  color: darkMode
                    ? getColors.primaryTextDark
                    : getColors.secondaryTextLight,
                  "&:hover": {
                    color: getColors.primaryTextDark,
                  },
                }}
                onClick={handleAddClicked}
              >
                Add Treatment
              </Button>
            </Box>
            <Box
              display="grid"
              gridTemplateColumns="repeat(3, 1fr)"
              gap={4}
              rowGap={4}
            >
              {filteredTreatments.map((treatment, index) => (
                <TreatmentItem
                  key={index}
                  {...treatment}
                  onTreatmentUpdate={updateTreatment}
                />
              ))}
            </Box>
          </>
        )}
      </Box>
      <AddTreatmentDialog
        open={openAddDialog}
        onClose={handleCancelAdd}
        onAdd={handleAddTreatment}
      />
    </>
  );
};

export default TreatmentPage;
